import { ClusterAndHeaderBoxMetadata } from '@/components/public/Visualisations/BubblesV2/Metadata'
import { Coordinate2D, Dimensions, Rectangle } from '@/types'

const SIDE_BUBBLES_CLUSTER_PADDING = 100
const ALL_CLUSTERS_BOUNDING_BOX_PADDING = 20
const MAX_ZOOM_FACTOR = 2
const VIEWBOX_INCREASE_STEP_AMOUNT = 1.05

export interface ClusterAndHeaderPositions {
  cluster: Coordinate2D
  header: Coordinate2D
}

export interface BubblesLayout {
  zoomFactor: number
  mainClustersPositionsByPath: Record<string, ClusterAndHeaderPositions>
  offset: Coordinate2D
  allEntitiesBoundingBox: Rectangle
  mainClustersBoundingBox: Rectangle
  sideBubblesClusterBoundingBox: Rectangle
}

export function calculateLayout(
  mainClustersBoxes: ClusterAndHeaderBoxMetadata[],
  sideBubblesClusterSize: Dimensions,
  hideSideBubblesPercentage: number,
  canvasSize: number,
  containerSizeOnCanvas: Dimensions,
  containerToCanvasScale: number,
): BubblesLayout {
  const { mainClusterBoxesRows, requiredViewBoxSize } = arrangeMainClusterBoxesInRows(
    mainClustersBoxes,
    canvasSize,
  )

  const { mainClustersPositionsByPath, mainClustersBoundingBox } =
    calculateMainClustersPositionsAndBoundingBox(mainClusterBoxesRows, requiredViewBoxSize)

  const sideBubblesClusterBoundingBox = calculateSideBubblesClusterBoundingBox(
    mainClustersBoundingBox,
    sideBubblesClusterSize,
  )

  const allEntitiesBoundingBox = calculateAllClustersBoundingBox(
    mainClustersBoundingBox,
    sideBubblesClusterBoundingBox,
  )

  const { zoomFactor, offset } = calculateTransformations(
    allEntitiesBoundingBox,
    sideBubblesClusterBoundingBox,
    hideSideBubblesPercentage,
    canvasSize,
    containerSizeOnCanvas,
    containerToCanvasScale,
  )

  return {
    mainClustersPositionsByPath,
    mainClustersBoundingBox,
    sideBubblesClusterBoundingBox,
    allEntitiesBoundingBox,
    zoomFactor,
    offset,
  }
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function arrangeMainClusterBoxesInRows(
  clustersBoxes: ClusterAndHeaderBoxMetadata[],
  canvasSize: number,
): { mainClusterBoxesRows: ClusterAndHeaderBoxMetadata[][]; requiredViewBoxSize: number } {
  let requiredViewBoxSize = canvasSize
  let fits = false
  let mainClusterBoxesRows: ClusterAndHeaderBoxMetadata[][] = []

  while (!fits) {
    mainClusterBoxesRows = []
    let currentRow: ClusterAndHeaderBoxMetadata[] = []
    let currentRowWidth = 0
    let totalHeight = 0
    fits = true // assume the boxes fit unless proven otherwise

    for (const box of clustersBoxes) {
      if (box.width > requiredViewBoxSize || box.height > requiredViewBoxSize) {
        requiredViewBoxSize = Math.max(box.width, box.height) * VIEWBOX_INCREASE_STEP_AMOUNT
        fits = false
        break
      }

      if (currentRowWidth + box.width > requiredViewBoxSize) {
        totalHeight += Math.max(...currentRow.map((b) => b.height))
        mainClusterBoxesRows.push(currentRow)
        currentRow = [box]
        currentRowWidth = box.width
        if (totalHeight > requiredViewBoxSize) {
          requiredViewBoxSize *= VIEWBOX_INCREASE_STEP_AMOUNT
          fits = false
          break
        }
      } else {
        currentRow.push(box)
        currentRowWidth += box.width
      }
    }

    if (fits && currentRow.length > 0) {
      totalHeight += Math.max(...currentRow.map((b) => b.height))
      mainClusterBoxesRows.push(currentRow)
      if (totalHeight > requiredViewBoxSize) {
        requiredViewBoxSize *= VIEWBOX_INCREASE_STEP_AMOUNT
        fits = false
      }
    }
  }

  return { mainClusterBoxesRows, requiredViewBoxSize }
}

function calculateMainClustersPositionsAndBoundingBox(
  clusterBoxesRows: ClusterAndHeaderBoxMetadata[][],
  viewBoxSize: number,
): {
  mainClustersPositionsByPath: Record<string, ClusterAndHeaderPositions>
  mainClustersBoundingBox: Rectangle
} {
  let y = 0
  const mainClustersPositionsByPath: Record<string, ClusterAndHeaderPositions> = {}
  const mainClustersBoundingBox: Rectangle = new Rectangle({
    top: Infinity,
    left: Infinity,
    bottom: -Infinity,
    right: -Infinity,
  })

  if (clusterBoxesRows.length === 0) {
    return {
      mainClustersPositionsByPath: {},
      mainClustersBoundingBox: new Rectangle({ top: 0, left: 0, bottom: 0, right: 0 }),
    }
  }

  clusterBoxesRows.forEach((row) => {
    const maxHeight = Math.max(...row.map((box) => box.height))
    let x = (viewBoxSize - row.reduce((sum, box) => sum + box.width, 0)) / 2 // Start x position centered horizontally

    row.forEach((cluster) => {
      const centerX = x + cluster.width / 2

      mainClustersPositionsByPath[cluster.path.toString()] = {
        cluster: {
          x: centerX,
          y: y + maxHeight / 2 + cluster.headerHeight,
        },
        header: {
          x: centerX,
          y: y + cluster.headerHeight,
        },
      }

      mainClustersBoundingBox.top = Math.min(mainClustersBoundingBox.top, y)
      mainClustersBoundingBox.left = Math.min(mainClustersBoundingBox.left, x)
      mainClustersBoundingBox.bottom = Math.max(mainClustersBoundingBox.bottom, y + maxHeight)
      mainClustersBoundingBox.right = Math.max(mainClustersBoundingBox.right, x + cluster.width)

      x += cluster.width // Move x to the next box start
    })

    y += maxHeight // Move y to the next row start
  })

  return {
    mainClustersPositionsByPath,
    mainClustersBoundingBox,
  }
}

function calculateSideBubblesClusterBoundingBox(
  mainClustersBoundingBox: Rectangle,
  sideBubblesClusterSize: Dimensions,
) {
  // only add padding to the side bubbles cluster if there is a cluster to pad
  const sideBubblesClusterPadding =
    sideBubblesClusterSize.width > 0 ? SIDE_BUBBLES_CLUSTER_PADDING : 0
  return new Rectangle({
    top: mainClustersBoundingBox.center.y - sideBubblesClusterSize.height / 2,
    left: mainClustersBoundingBox.right + sideBubblesClusterPadding,
    bottom: mainClustersBoundingBox.center.y + sideBubblesClusterSize.height / 2,
    right: mainClustersBoundingBox.right + sideBubblesClusterSize.width + sideBubblesClusterPadding,
  })
}

function calculateAllClustersBoundingBox(
  mainClustersBoundingBox: Rectangle,
  sideBubblesClusterBoundingBox: Rectangle,
) {
  return new Rectangle({
    top:
      Math.min(mainClustersBoundingBox.top, sideBubblesClusterBoundingBox.top) -
      ALL_CLUSTERS_BOUNDING_BOX_PADDING,
    left: mainClustersBoundingBox.left - ALL_CLUSTERS_BOUNDING_BOX_PADDING,
    bottom:
      Math.max(mainClustersBoundingBox.bottom, sideBubblesClusterBoundingBox.bottom) +
      ALL_CLUSTERS_BOUNDING_BOX_PADDING,
    right: sideBubblesClusterBoundingBox.right + ALL_CLUSTERS_BOUNDING_BOX_PADDING,
  })
}

function calculateTransformations(
  allEntitiesBoundingBox: Rectangle,
  sideBubblesClusterBoundingBox: Rectangle,
  hideSideBubblesPercentage: number,
  canvasSize: number,
  containerSizeOnCanvas: Dimensions,
  containerToCanvasScale: number,
) {
  const hideWidth =
    sideBubblesClusterBoundingBox.width * (hideSideBubblesPercentage / 100)
  const adjustedAllEntitiesWidth = allEntitiesBoundingBox.width - hideWidth

  const zoomFactorX = containerSizeOnCanvas.width / adjustedAllEntitiesWidth
  const zoomFactorY = containerSizeOnCanvas.height / allEntitiesBoundingBox.height
  const zoomFactor = Math.min(zoomFactorX, zoomFactorY, MAX_ZOOM_FACTOR)
  const offsetZoomAdjustment = zoomFactor * containerToCanvasScale
  const offset = {
    x:
      (canvasSize / 2 - (allEntitiesBoundingBox.left + (allEntitiesBoundingBox.width - hideWidth) / 2)) *
      offsetZoomAdjustment,
    y: (canvasSize / 2 - allEntitiesBoundingBox.center.y) * offsetZoomAdjustment,
  }
  return { zoomFactor, offset }
}
